import { useState } from 'react';
import { ProjectResponse } from '@shared/interfaces/api/trackerRequest';
import './TrackerHeader.sass';

interface TrackerHeaderProps {
  project: ProjectResponse;
  onRegenerateTracker: () => void;
  isRegenerating: boolean;
}

function TrackerHeader({ project, onRegenerateTracker, isRegenerating }: TrackerHeaderProps) {
  const [showEmbedCode, setShowEmbedCode] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  const handleCopyEmbedCode = async () => {
    try {
      await navigator.clipboard.writeText(project.embed_code);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  return (
    <div className="tracker-header">
      <div className="header-content">
        <div className="project-info">
          <h1>{project.name}</h1>
          <p className="project-domain">{project.domain}</p>
          <div className="project-meta">
            <span className="tracker-id">
              <strong>Tracker ID:</strong> {project.tracker_id}
            </span>
            <span className="tracker-version">
              <strong>Version:</strong> v{project.tracker_version}
            </span>
            <span className={`status ${project.is_active ? 'active' : 'inactive'}`}>
              {project.is_active ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>

        <div className="header-actions">
          <button
            className="btn-secondary"
            onClick={() => setShowEmbedCode(!showEmbedCode)}
            type="button"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M5 11L2 8L5 5M11 5L14 8L11 11M9 3L7 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            {showEmbedCode ? 'Hide' : 'Show'} Embed Code
          </button>

          <button
            className="btn-primary"
            onClick={onRegenerateTracker}
            disabled={isRegenerating}
            type="button"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M13 8C13 10.7614 10.7614 13 8 13C5.23858 13 3 10.7614 3 8C3 5.23858 5.23858 3 8 3C9.36 3 10.6 3.54 11.5 4.4M11.5 4.4V2M11.5 4.4H9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            {isRegenerating ? 'Regenerating...' : 'Regenerate Tracker'}
          </button>
        </div>
      </div>

      {showEmbedCode && (
        <div className="embed-code-section">
          <div className="code-header">
            <span>Embed Code</span>
            <button
              className={`copy-button ${isCopied ? 'copied' : ''}`}
              onClick={handleCopyEmbedCode}
              type="button"
            >
              {isCopied ? (
                <>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M13.5 4.5L6 12L2.5 8.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Copied
                </>
              ) : (
                <>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <rect x="5" y="5" width="9" height="9" rx="1" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                    <path d="M3 11V3C3 2.44772 3.44772 2 4 2H10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                  Copy
                </>
              )}
            </button>
          </div>
          <pre className="code-content">
            <code>{project.embed_code}</code>
          </pre>
        </div>
      )}
    </div>
  );
}

export default TrackerHeader;
